import { computed, ref, watchEffect } from "vue";
import { asCur, isPercentage } from "@/helpers/inputHelpers";
import useKlantgegevens from "./useKlantgegevens";
import { getWoonquote } from "@/helpers/woonquoteHelpers";
import type {
	ToetsHuishoudenParameters,
	ToetsHuishoudenWoonqoute,
} from "@/models/ToetsHuishouden";
import { useMainStore } from "@/store/useMainStore";
import type { MortgagePart } from "@/models/Leningdeel";
import { useParameters } from "@/store/useParameters";
import { defineStore, storeToRefs } from "pinia";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { calculateTotalYearlyIncome } from "@/helpers/inventarisation/incomeCalculations";
import type {
	InventarisationLiabilities,
	LeningdelenTotaaldeel,
} from "@loanlink-nl/model-app";
import { getMonthSpan } from "@loanlink-nl/shared";
import { useCalculationDate } from "@/composables/useCalculationDate";
import { isEligibleForAOW } from "@/composables/useAOWEligibility";
import { fuzzyDateParse } from "@loanlink-nl/migration";

export interface LeningdelenComputed {
	maximaleLooptijd?: number;
	maxLeningInkomenLasten?: number;
	toegestaneMaandlast?: number;
	toetsAnnuiteitenFactor?: number | null;
	toetsinkomen?: number;
	toetsinkomenPerMaand?: number;
	toetsinkomenPerMaandTbvWQ?: number;
	toetsinkomenTbvWQ?: number;
	toetsinkomenTbvWQPerMaand?: number;
	woonquoteBox1?: number | null;
	woonquoteBox3?: number | null;
}

/**
 * Berekent de jaarlast partneralimentatie voor de toetsing.
 *
 * Bron: de `spousalAlimony`-secties uit de inventarisatie (dezelfde bron die
 * naar HDN wordt gemapt). Een sectie telt mee als hij actief is: `endDate`
 * leeg/ongeldig OF op/na de rekendatum. Verlopen secties worden genegeerd.
 * Dit volgt hetzelfde actief-patroon als `incomeCalculations` (paymentEndDate).
 *
 * Fallback voor bestaande dossiers zonder `spousalAlimony`-secties maar met
 * het oude `alimentatiePerMaand`-veld: maandbedrag × 12.
 */
export function getAlimentatiePerJaar(
	liabilities: InventarisationLiabilities,
	alimentatiePerMaandFallback: number | undefined,
	calculationDateString: string,
): number {
	const asOfDateTime = fuzzyDateParse(calculationDateString);

	let jaarlastUitSecties = 0;
	let heeftSpousalAlimonySectie = false;

	for (const customer of liabilities.byCustomer ?? []) {
		for (const sectie of customer.sections?.spousalAlimony ?? []) {
			heeftSpousalAlimonySectie = true;

			const endDateTime = fuzzyDateParse(sectie.values?.endDate);
			if (
				asOfDateTime.isValid &&
				endDateTime.isValid &&
				endDateTime < asOfDateTime
			) {
				continue;
			}

			jaarlastUitSecties += sectie.values?.annualAmount ?? 0;
		}
	}

	if (heeftSpousalAlimonySectie) {
		return jaarlastUitSecties;
	}

	// Fallback: geen inventarisatie-secties → oud maandveld × 12.
	return (alimentatiePerMaandFallback ?? 0) * 12;
}

export default defineStore("leningdelen", () => {
	const klantgegevens = useKlantgegevens();
	const {
		calculationDate,
		calculationDateString,
		passingDate,
		passingDateString,
	} = useCalculationDate();

	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	const computedValues = ref<LeningdelenComputed>({});

	const mortgagePartsTotal = computed<LeningdelenTotaaldeel>(() => {
		const totalHeight = getTotalHeight(data.value.mortgage.mortgageParts);

		const toetslastTotal = getTotalToetslastPerMaand(computedLeningdelen.value);

		return {
			id: crypto.randomUUID(),
			hoogte: totalHeight,
			toetslastPerMaand: toetslastTotal,
			rente: weightedInterest(
				data.value.mortgage.mortgageParts,
				"interestRate",
			),
			toetsrente: weightedInterest(computedLeningdelen.value, "toetsrente"),
			looptijd: getMaxlooptijd(data.value.mortgage.mortgageParts),
		};
	});

	const newMortgagePartsTotal = computed<LeningdelenTotaaldeel>(() => {
		const totalHeight = getTotalHeight(
			data.value.mortgage.mortgageParts.filter(
				(part) => part.existingMortgagePartId === undefined,
			),
		);
		return {
			id: crypto.randomUUID(),
			hoogte: totalHeight,
		};
	});

	const computedLeningdelen = computed(() => {
		return data.value.mortgage.mortgageParts.map((part) => {
			const toetsrente = getToetsrente(part);
			const toetslastPerMaand = getToetslast(part, toetsrente);

			const durationMonths =
				part.durationMonths ??
				(part.endDate
					? getMonthSpan(passingDate.value, part.endDate)
					: undefined);

			return {
				toetsrente,
				toetslastPerMaand,
				durationMonths,
				amount: part.amount,
			};
		});
	});

	function getToetslast(part: MortgagePart, toetsrente?: number) {
		const leninghoogte = asCur(part.amount);
		const durationMonths =
			part.durationMonths ??
			(part.endDate
				? getMonthSpan(passingDate.value, part.endDate)
				: undefined);

		// Determine if the part is Box 1 (deductible) or Box 3 (non-deductible)
		const isBox1 = part.interestDeductionLaw !== "none";

		if (!Number.isFinite(toetsrente)) {
			return undefined;
		}

		const toetslastPerMaand = getToetslastPerMaand(
			leninghoogte,
			toetsrente !== undefined ? toetsrente : NaN,
			durationMonths,
			isBox1,
		);

		return toetslastPerMaand;
	}

	function getToetsrente(part: MortgagePart) {
		const rente = asCur(part.interestRate);

		let fixedInterestMonths: number | undefined;
		if (part.fixedInterestMonths !== undefined) {
			fixedInterestMonths = asCur(part.fixedInterestMonths);
		} else if (part.fixedInterestEndDate) {
			fixedInterestMonths = getMonthSpan(
				passingDateString.value,
				part.fixedInterestEndDate,
			);
		}

		if (fixedInterestMonths === undefined) {
			fixedInterestMonths = Number.POSITIVE_INFINITY;
		}

		const fictieveToetsrente = useParameters(
			calculationDateString.value,
		).fictieveToetsrente;
		const fictieveToetsrenteGrens = useParameters(
			calculationDateString.value,
		).fictieveToetsrenteGrens;

		const toetsrente =
			fixedInterestMonths !== undefined &&
			rente !== undefined &&
			fixedInterestMonths < fictieveToetsrenteGrens &&
			fictieveToetsrente > rente
				? fictieveToetsrente
				: rente;

		return toetsrente;
	}

	function removeLeningdeel(id: string) {
		const newIndex = data.value.mortgage.mortgageParts.findIndex(
			(part) => part.id === id,
		);

		if (newIndex >= 0) {
			data.value.mortgage.mortgageParts.splice(newIndex, 1);
		}
	}

	function getDefaultAddLeningdeelOpts(): Partial<MortgagePart> {
		return {
			repaymentForm: "annuity",
			interestDeductionLaw: "new",
			durationMonths: 360,
			interestDeductionEndDate: passingDate.value
				.plus({ years: 30 })
				.toISODate(),
		};
	}

	function addLeningdeel(
		newLeningdeelOpts: Partial<MortgagePart> = getDefaultAddLeningdeelOpts(),
	) {
		data.value.mortgage.mortgageParts.push({
			id: crypto.randomUUID(),
			...newLeningdeelOpts,
		});
	}

	function updateValue<T extends keyof MortgagePart>(
		partId: string,
		key: T,
		newValue: MortgagePart[T],
	) {
		const part = data.value.mortgage.mortgageParts.find(
			(part) => part.id === partId,
		);
		if (part) {
			part[key] = newValue;
		}
	}

	/**
	 * Set the loanpart names
	 */
	watchEffect(() => {
		data.value.mortgage.mortgageParts.forEach((part) => {
			part.id ??= crypto.randomUUID();
		});
	});

	function getTotalHeight(parts: MortgagePart[]) {
		let total = 0.0;

		for (const deel of parts) {
			const hoogte = asCur(deel.amount);

			if (hoogte !== undefined && Number.isFinite(hoogte)) {
				total += hoogte;
			}
		}

		return total;
	}

	function getTotalToetslastPerMaand(parts: { toetslastPerMaand?: number }[]) {
		let toetslastTotaal = 0.0;

		for (const deel of parts) {
			const toetslastPerMaand = asCur(deel.toetslastPerMaand);
			if (
				toetslastPerMaand !== undefined &&
				Number.isFinite(toetslastPerMaand)
			) {
				toetslastTotaal += toetslastPerMaand;
			}
		}

		return toetslastTotaal;
	}

	// Gewogen rentepercentage
	function weightedInterest<T extends "toetsrente" | "interestRate">(
		parts: ({ [key in T]?: number } & {
			amount?: number;
			durationMonths?: number;
			endDate?: string;
		})[],
		renteSoort: T,
	) {
		let totaalPercentage = 0.0;
		let deelfactor = 0.0;

		for (const deel of parts) {
			const rente = asCur(deel[renteSoort]);
			const hoogte = asCur(deel.amount) ?? 0;
			const looptijd =
				asCur(deel.durationMonths) ??
				(deel.endDate
					? getMonthSpan(passingDateString.value, deel.endDate)
					: 1);

			if (
				rente !== undefined &&
				Number.isFinite(rente) &&
				looptijd !== undefined
			) {
				totaalPercentage += rente * hoogte * looptijd;
			}

			if (Number.isFinite(hoogte) && looptijd !== undefined) {
				deelfactor += hoogte * looptijd;
			}
		}

		totaalPercentage = totaalPercentage / deelfactor;
		if (!Number.isFinite(totaalPercentage)) {
			totaalPercentage = 0.0;
		}

		return totaalPercentage;
	}

	function getMaxlooptijd(parts: MortgagePart[]) {
		let maximaleDuur = 0;

		for (const deel of parts) {
			let looptijd;
			if (deel.durationMonths !== undefined) {
				looptijd = asCur(deel.durationMonths);
			} else if (deel.endDate !== undefined) {
				looptijd = getMonthSpan(passingDateString.value, deel.endDate);
			}

			if (
				looptijd !== undefined &&
				Number.isFinite(looptijd) &&
				looptijd > maximaleDuur
			) {
				maximaleDuur = looptijd;
			}
		}

		return maximaleDuur;
	}

	function getToetslastPerMaand(
		hoogte: number | undefined,
		toetsrente: number,
		looptijd: number | undefined,
		box1: boolean,
	) {
		const toetsrentePerMaand = toetsrente / 12;

		const annuiteitenfactor =
			looptijd !== undefined
				? toetsrentePerMaand / (1 - Math.pow(1 + toetsrentePerMaand, -looptijd))
				: NaN;

		let toetslastPerMaand: number | undefined =
			hoogte !== undefined ? hoogte * annuiteitenfactor : undefined;

		if (!box1) {
			const woonquoteBox1 = computedValues.value.woonquoteBox1;
			const woonquoteBox3 = computedValues.value.woonquoteBox3;

			if (
				typeof woonquoteBox1 !== "number" ||
				typeof woonquoteBox3 !== "number"
			) {
				toetslastPerMaand = undefined;
			} else {
				toetslastPerMaand =
					toetslastPerMaand !== undefined
						? (toetslastPerMaand * woonquoteBox1) / woonquoteBox3
						: NaN;
			}
		} else if (toetsrentePerMaand === 0.0) {
			toetslastPerMaand =
				hoogte !== undefined && looptijd !== undefined
					? hoogte / looptijd
					: NaN;
		}

		if (Number.isFinite(toetslastPerMaand)) {
			return toetslastPerMaand;
		}
		return undefined;
	}

	function getMaandlast(hoogte: number, rente: number, looptijd: number) {
		const rentePerMaand = rente / 12;

		const annuiteitenFactor =
			rentePerMaand / (1 - Math.pow(1 + rentePerMaand, -looptijd));

		let lastPerMaand = hoogte * annuiteitenFactor;

		if (rentePerMaand === 0.0) {
			lastPerMaand = hoogte / looptijd;
		}

		if (Number.isFinite(lastPerMaand)) {
			return lastPerMaand;
		}
		return NaN;
	}

	// Bereken toetsinkomen en toetsinkomen per maand
	watchEffect(() => {
		// Calculate income from inventarisation data instead of klantgegevens
		const incomeCalculation = calculateTotalYearlyIncome(
			data.value.income,
			data.value.customers.map((customer) => customer.id),
			calculationDateString.value,
		);
		const inkomenKlant = incomeCalculation.customer1Income;
		const inkomenPartner = incomeCalculation.customer2Income;

		if (!Number.isFinite(inkomenKlant) && !Number.isFinite(inkomenPartner)) {
			return;
		}

		const hoogsteInkomen = Math.max(inkomenKlant ?? 0, inkomenPartner ?? 0);
		const laagsteInkomen = Math.min(inkomenKlant ?? 0, inkomenPartner ?? 0);

		const alimentatiePerJaar = getAlimentatiePerJaar(
			data.value.liabilities,
			asCur(klantgegevens.state.values.alimentatiePerMaand),
			calculationDateString.value,
		);

		const gecombineerdInkomen = hoogsteInkomen + laagsteInkomen;
		const toetsinkomen = gecombineerdInkomen - alimentatiePerJaar;
		const toetsinkomenPerMaand = toetsinkomen / 12;

		computedValues.value.toetsinkomen = toetsinkomen;
		computedValues.value.toetsinkomenPerMaand = toetsinkomenPerMaand;

		const gecombineerdInkomenTbvWoonquote =
			hoogsteInkomen +
			laagsteInkomen *
				useParameters(calculationDateString.value).factorTweedeInkomen;

		const toetsinkomenTbvWoonquote =
			gecombineerdInkomenTbvWoonquote - alimentatiePerJaar;
		const toetsinkomenPerMaandTbvWoonquote = toetsinkomenTbvWoonquote / 12;

		computedValues.value.toetsinkomenTbvWQ = toetsinkomenTbvWoonquote;
		computedValues.value.toetsinkomenTbvWQPerMaand =
			toetsinkomenPerMaandTbvWoonquote;
	});

	// Bereken woonquotes
	watchEffect(() => {
		// Calculate income from inventarisation data instead of klantgegevens
		const incomeCalculation = calculateTotalYearlyIncome(
			data.value.income,
			data.value.customers.map((customer) => customer.id),
			calculationDateString.value,
		);
		const inkomenKlant = incomeCalculation.customer1Income;
		const inkomenPartner = incomeCalculation.customer2Income;

		// Calculate AOW eligibility based on customer birth dates
		const klantBirthDate = data.value.customers[0]?.personDetails?.birthDate;
		const partnerBirthDate = data.value.customers[1]?.personDetails?.birthDate;
		const klantAOW = isEligibleForAOW(
			klantBirthDate,
			calculationDateString.value,
		);
		const partnerAOW = isEligibleForAOW(
			partnerBirthDate,
			calculationDateString.value,
		);

		const aow = inkomenKlant >= inkomenPartner ? klantAOW : partnerAOW;

		const toetsinkomen = computedValues.value.toetsinkomenTbvWQ;

		const woonquoteRenteAfrondFactor = useParameters(
			calculationDateString.value,
		).woonquoteRenteAfrondFactor;

		const toetsrente = mortgagePartsTotal.value.toetsrente;
		const gemiddeldeRenteLeningdelen =
			typeof toetsrente === "number"
				? Math.round(toetsrente * woonquoteRenteAfrondFactor) /
					woonquoteRenteAfrondFactor
				: NaN;

		const jaarFinLastPercentages = calculationDate.value.year;

		const toetshuishouden: ToetsHuishoudenParameters = {
			toetsinkomen: toetsinkomen ?? 0,
			aow,
			box1: true,
		};
		const woonquoteBox1 = getWoonquote(
			toetshuishouden,
			gemiddeldeRenteLeningdelen,
			jaarFinLastPercentages,
		);

		toetshuishouden.box1 = false;
		const woonquoteBox3 = getWoonquote(
			toetshuishouden,
			gemiddeldeRenteLeningdelen,
			jaarFinLastPercentages,
		);

		computedValues.value.woonquoteBox1 = woonquoteBox1 ?? 0;
		computedValues.value.woonquoteBox3 = woonquoteBox3 ?? 0;
	});

	function getToegestaneMaandlast(huishouden: ToetsHuishoudenWoonqoute) {
		const fictieveKredietlastPerMaand =
			(huishouden.bkrVermelding ?? 0) *
			useParameters(calculationDateString.value).fictiefKredietRekenpercentage;

		const toegestaneMaandlast =
			Math.round(
				((huishouden.toetsinkomen / 12) * huishouden.woonquote -
					fictieveKredietlastPerMaand -
					(huishouden.erfpachtlastPerMaand ?? 0)) *
					100,
			) / 100;

		return toegestaneMaandlast;
	}

	// Bereken toegestane maandlast
	watchEffect(() => {
		// Studielening kan meegenomen worden. Gebeurt nu (nog) niet.
		const toetsinkomen = asCur(computedValues.value.toetsinkomen);

		const bkrVermelding = asCur(klantgegevens.state.values.bkrVermelding) ?? 0;

		const erfpachtlastPerMaand =
			(asCur(data.value.objectData.erfpacht?.canonYearly) ?? 0) / 12;

		const woonquote = isPercentage(computedValues.value.woonquoteBox1) ?? 0;

		const toetsHuishouden: ToetsHuishoudenWoonqoute = {
			toetsinkomen: toetsinkomen ?? 0,
			bkrVermelding,
			erfpachtlastPerMaand,
			woonquote,
		};

		const toegestaneMaandlast = getToegestaneMaandlast(toetsHuishouden);

		if (Number.isFinite(toegestaneMaandlast)) {
			computedValues.value.toegestaneMaandlast = toegestaneMaandlast;
		} else {
			computedValues.value.toegestaneMaandlast = undefined;
		}
	});

	const store = useMainStore();

	watchEffect(() => {
		const hypotheekhoogte = asCur(mortgagePartsTotal.value.hoogte);
		const maximaleHoogte = computedValues.value.maxLeningInkomenLasten;

		if (
			!maximaleHoogte ||
			(hypotheekhoogte && hypotheekhoogte > maximaleHoogte)
		) {
			store.leningdelenTeHoog = true;
		} else {
			store.leningdelenTeHoog = false;
		}
	});

	// Bereken maximale looptijd
	watchEffect(() => {
		const maximaleLooptijd = asCur(mortgagePartsTotal.value.looptijd);

		computedValues.value.maximaleLooptijd = maximaleLooptijd;
	});

	function getAnnuiteitenfactor(toetsrente: number, looptijd: number) {
		const rentePerMaand = toetsrente / 12;

		const annuiteitenFactor =
			rentePerMaand / (1 - Math.pow(1 + rentePerMaand, -1 * looptijd));

		return annuiteitenFactor;
	}

	// Bereken toestAnnuiteitenfactor
	watchEffect(() => {
		const toetsrente = isPercentage(mortgagePartsTotal.value.toetsrente);
		const maximaleLooptijd = computedValues.value.maximaleLooptijd;

		let toetsAnnuiteitenFactor: number | undefined;
		if (typeof maximaleLooptijd !== "number") {
			toetsAnnuiteitenFactor = undefined;
		} else {
			toetsAnnuiteitenFactor = getAnnuiteitenfactor(
				toetsrente !== undefined ? toetsrente : NaN,
				maximaleLooptijd,
			);
		}

		computedValues.value.toetsAnnuiteitenFactor = toetsAnnuiteitenFactor;
	});

	function getMaxLening(
		toegestaneMaandlast: number,
		fictieveMaandlast: number,
		toetsAnnuiteitenFactor: number,
		leningHoogte: number,
	): number {
		const ruimte = toegestaneMaandlast - fictieveMaandlast;
		const meerMinderLenen = ruimte / toetsAnnuiteitenFactor;
		const maxLening = leningHoogte + meerMinderLenen;
		return maxLening;
	}

	const maxLeningExtra = computed(() => {
		let maxLeningExtra = 0;
		if (calculationDateString.value >= "2024") {
			const extraLeenbedragenEnergielabel = useParameters(
				calculationDateString.value,
			).extraLeenbedragenEnergielabel;

			const energieLabel = dossierDataStore.data.objectData.energielabel;

			const extraLenenEnergielabel = energieLabel
				? (extraLeenbedragenEnergielabel[energieLabel] ?? 0)
				: 0;

			maxLeningExtra += extraLenenEnergielabel;

			if (data.value.customers.length === 1) {
				const birthDate = data.value.customers[0]?.personDetails?.birthDate;
				const aow = isEligibleForAOW(birthDate, calculationDateString.value);
				const incomeCalculation = calculateTotalYearlyIncome(
					data.value.income,
					data.value.customers.map((customer) => customer.id),
					calculationDateString.value,
				);

				if (
					incomeCalculation.totalIncome >
					useParameters(calculationDateString.value).extraLenenGrens(aow)
				) {
					const extraLenenAlleenstaand =
						useParameters(calculationDateString.value).extraLenenAlleenstaand ??
						0;

					maxLeningExtra += extraLenenAlleenstaand;
				}
			}
		}

		return maxLeningExtra;
	});

	function getMaxLeningTotal(
		toegestaneMaandlast?: number,
		fictieveMaandlast?: number | string,
		toetsAnnuiteitenFactor?: number | null,
		leningHoogte?: number | string,
	) {
		let maxLening: number | undefined;

		if (
			typeof toegestaneMaandlast === "number" &&
			typeof fictieveMaandlast === "number" &&
			typeof toetsAnnuiteitenFactor === "number" &&
			typeof leningHoogte === "number"
		) {
			maxLening = getMaxLening(
				toegestaneMaandlast,
				fictieveMaandlast,
				toetsAnnuiteitenFactor,
				leningHoogte,
			);
			maxLening += maxLeningExtra.value;
		}

		if (!Number.isFinite(maxLening)) {
			maxLening = undefined;
		} else if (maxLening !== undefined && maxLening <= 0) {
			maxLening = 0;
		}

		return maxLening;
	}

	// Bereken maximale hypotheek obv. inkomen en lasten
	watchEffect(() => {
		const toegestaneMaandlast = computedValues.value.toegestaneMaandlast;
		const fictieveMaandlast = mortgagePartsTotal.value.toetslastPerMaand;

		const toetsAnnuiteitenFactor = computedValues.value.toetsAnnuiteitenFactor;
		const leningHoogte = mortgagePartsTotal.value.hoogte;

		computedValues.value.maxLeningInkomenLasten = getMaxLeningTotal(
			toegestaneMaandlast,
			fictieveMaandlast,
			toetsAnnuiteitenFactor,
			leningHoogte,
		);
	});

	return {
		computedValues,
		maxLeningExtra,
		mortgagePartsTotal,
		computedLeningdelen,
		newMortgagePartsTotal,
		getToetslastPerMaand,
		getToegestaneMaandlast,
		getMaandlast,
		getAnnuiteitenfactor,
		getMaxLeningTotal,
		addLeningdeel,
		removeLeningdeel,
		updateValue,
	};
});
